import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  LogOut, Mail, Users, Images, Settings, LayoutDashboard, Newspaper,
  FileText, LayoutPanelTop, CalendarHeart, Tags, FolderOpen, ActivitySquare, UserRound, PlayCircle,
} from "lucide-react";
import Logo from "../components/Logo";
import { api } from "../lib/api";
import { useLang } from "../i18n";
import DashboardHome from "./admin/DashboardHome";
import GalleryManager from "./admin/GalleryManager";
import BlogManager from "./admin/BlogManager";
import DocManager from "./admin/DocManager";
import BannerManager from "./admin/BannerManager";
import EventManager from "./admin/EventManager";
import CategoryManager from "./admin/CategoryManager";
import MediaManager from "./admin/MediaManager";
import SiteContentManager from "./admin/SiteContentManager";
import ActivitiesManager from "./admin/ActivitiesManager";
import FounderManager from "./admin/FounderManager";
import VideoManager from "./admin/VideoManager";

function Messages() {
  const [items, setItems] = useState(null);
  useEffect(() => {
    api.get("/admin/messages").then((r) => setItems(r.data.items)).catch(() => setItems([]));
  }, []);
  if (!items) return <p className="text-ngo-ink/50 py-10 text-center">...</p>;
  if (!items.length) return <p data-testid="messages-empty" className="text-ngo-ink/50 py-10 text-center">अभी कोई संदेश नहीं है।</p>;
  return (
    <div className="space-y-4" data-testid="messages-list">
      {items.map((m) => (
        <div key={m.id} className="bg-white rounded-2xl border border-ngo-deep/10 p-5">
          <div className="flex flex-wrap justify-between gap-2">
            <p className="font-semibold text-ngo-ink">{m.name} <span className="font-normal text-ngo-ink/50">• {m.phone}</span></p>
            <p className="text-xs text-ngo-ink/45">{new Date(m.created_at).toLocaleString("hi-IN")}</p>
          </div>
          <p className="mt-1 text-sm font-medium text-ngo-deep">{m.subject}</p>
          {m.email && <p className="text-xs text-ngo-ink/55">{m.email}</p>}
          <p className="mt-2 text-sm text-ngo-ink/70 leading-relaxed">{m.message}</p>
        </div>
      ))}
    </div>
  );
}

function Volunteers() {
  const [items, setItems] = useState(null);
  useEffect(() => {
    api.get("/admin/volunteers").then((r) => setItems(r.data.items)).catch(() => setItems([]));
  }, []);
  if (!items) return <p className="text-ngo-ink/50 py-10 text-center">...</p>;
  if (!items.length) return <p data-testid="volunteers-empty" className="text-ngo-ink/50 py-10 text-center">अभी कोई आवेदन नहीं है।</p>;
  return (
    <div className="space-y-4" data-testid="volunteers-list">
      {items.map((v) => (
        <div key={v.id} className="bg-white rounded-2xl border border-ngo-deep/10 p-5">
          <div className="flex flex-wrap justify-between gap-2">
            <p className="font-semibold text-ngo-ink">{v.name} <span className="font-normal text-ngo-ink/50">• {v.phone} • {v.city}</span></p>
            <p className="text-xs text-ngo-ink/45">{new Date(v.created_at).toLocaleString("hi-IN")}</p>
          </div>
          <p className="mt-1 text-sm font-medium text-ngo-deep">{v.interest}</p>
          <p className="mt-1 text-sm text-ngo-ink/70">{v.contribution}</p>
          {v.message && <p className="mt-2 text-sm text-ngo-ink/60 leading-relaxed">{v.message}</p>}
        </div>
      ))}
    </div>
  );
}

const TABS = [
  { key: "overview", icon: LayoutDashboard, label: "अवलोकन" },
  { key: "gallery", icon: Images, label: "फोटो गैलरी" },
  { key: "activities", icon: ActivitySquare, label: "मुख्य गतिविधियां" },
  { key: "blogs", icon: Newspaper, label: "ब्लॉग / समाचार" },
  { key: "documents", icon: FileText, label: "दस्तावेज़ / PDF" },
  { key: "banners", icon: LayoutPanelTop, label: "बैनर" },
  { key: "events", icon: CalendarHeart, label: "कार्यक्रम" },
  { key: "videos", icon: PlayCircle, label: "वीडियो" },
  { key: "founder", icon: UserRound, label: "संस्थापक" },
  { key: "categories", icon: Tags, label: "श्रेणियां" },
  { key: "media", icon: FolderOpen, label: "मीडिया लाइब्रेरी" },
  { key: "messages", icon: Mail, label: "संदेश" },
  { key: "volunteers", icon: Users, label: "स्वयंसेवक" },
  { key: "content", icon: Settings, label: "साइट सामग्री" },
];

export default function AdminDashboard() {
  const { t } = useLang();
  const navigate = useNavigate();
  const [user, setUser] = useState(null);
  const [tab, setTab] = useState("overview");

  useEffect(() => {
    api.get("/auth/me").then((r) => setUser(r.data)).catch(() => navigate("/admin/login"));
  }, [navigate]);

  const logout = async () => {
    await api.post("/auth/logout").catch(() => {});
    navigate("/admin/login");
  };

  if (!user) return <main className="min-h-screen bg-ngo-cream flex items-center justify-center text-ngo-ink/50">...</main>;

  return (
    <main className="min-h-screen bg-ngo-cream">
      <div className="bg-ngo-forest text-ngo-cream sticky top-0 z-40">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Logo size={36} />
            <div>
              <h1 data-testid="admin-dashboard-heading" className="font-heading font-bold text-lg leading-tight">{t("admin.dashboard")}</h1>
              <p className="text-[11px] text-ngo-mint/60">{user.email}</p>
            </div>
          </div>
          <button data-testid="admin-logout" onClick={logout} className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-white/10 text-sm font-semibold hover:bg-white/20 transition-colors duration-200">
            <LogOut size={15} /> {t("admin.logout")}
          </button>
        </div>
      </div>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 py-8">
        <div className="flex flex-wrap gap-2.5 mb-8" role="tablist">
          {TABS.map((tb) => (
            <button
              key={tb.key}
              data-testid={`admin-tab-${tb.key}`}
              role="tab"
              aria-selected={tab === tb.key}
              onClick={() => setTab(tb.key)}
              className={`inline-flex items-center gap-2 px-4 py-2.5 rounded-full text-sm font-semibold transition-colors duration-200 ${
                tab === tb.key ? "bg-ngo-deep text-white" : "bg-white text-ngo-deep border border-ngo-deep/20 hover:bg-ngo-mint"
              }`}
            >
              <tb.icon size={15} /> {tb.label}
            </button>
          ))}
        </div>
        {tab === "overview" && <DashboardHome />}
        {tab === "gallery" && <GalleryManager />}
        {tab === "activities" && <ActivitiesManager />}
        {tab === "blogs" && <BlogManager />}
        {tab === "documents" && <DocManager />}
        {tab === "banners" && <BannerManager />}
        {tab === "events" && <EventManager />}
        {tab === "videos" && <VideoManager />}
        {tab === "founder" && <FounderManager />}
        {tab === "categories" && <CategoryManager />}
        {tab === "media" && <MediaManager />}
        {tab === "messages" && <Messages />}
        {tab === "volunteers" && <Volunteers />}
        {tab === "content" && <SiteContentManager />}
      </div>
    </main>
  );
}
